import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Anchor, MapPin, Waves } from "lucide-react";
import PortThumbnail from "@/components/PortThumbnail";
import PortMapDialog from "@/components/PortMapDialog";

/**
 * A single proportional timeline for a guest's charter.
 *
 * All stops (embark + intermediate ports + disembark) sit on the same
 * horizontal line, positioned by their arrival date as a percentage of
 * the total voyage span. When two icons collide, the last hovered/touched
 * one is raised to the top so nothing gets lost behind another thumbnail.
 *
 * Falls back to even spacing when the voyage span isn't computable
 * (e.g. missing embark_at or disembark_at).
 */

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

function daysBetween(a, b) {
  if (!a || !b) return null;
  try { return (new Date(b).getTime() - new Date(a).getTime()) / 86_400_000; }
  catch { return null; }
}

export default function JourneyTimeline({
  embarkPort, embarkAt,
  disembarkPort, disembarkAt,
  stops = [],
  daysRemaining, daysAboard,
  hideCopy = false,
  compact = false,
  // Per-guest windowing — on the personal `/journey` page we hide stops
  // outside a guest's embark/disembark window so they see THEIR voyage.
  // On the SHARED iPad cabin view the whole charter's public itinerary
  // must show regardless of any per-guest cutoff, so the caller passes
  // `filterStopsToWindow={false}`.
  filterStopsToWindow = true,
  // Developer switch — when off, tapping a stop does nothing and the
  // circular port map never mounts. Controlled from Platform Tools →
  // Journey maps. Defaults to enabled so nothing changes for installs
  // that never touch the switch.
  portMapEnabled = true,
}) {
  const [lastTouched, setLastTouched] = useState(null);
  // Clicking a node reveals an elegant golden circular map popup with a
  // spin-dial interaction — index into the `nodes` array so the dialog
  // can navigate between every stop by rotating the ring.
  const [mapIndex, setMapIndex] = useState(null);
  const containerRef = useRef(null);

  const nodes = useMemo(() => {
    // Build the ordered list of nodes: **Embark is ALWAYS the first
    // node** and **Disembark is ALWAYS the last**, regardless of the
    // dates in the arrays. Middle stops sort chronologically by
    // `arrive_at`, with `sort_index` as tie-breaker so the manager's
    // drag-drop order controls same-day ports.
    //
    // Per-guest window filter — this timeline is rendered on the guest's
    // own /journey page, so if a guest embarks late or disembarks early
    // (a real charter scenario — different guests join / leave on
    // different days) we hide the intermediate stops that fall outside
    // their personal window. Their embark and disembark endpoints are
    // still shown so the shape of THEIR voyage reads at a glance.
    const embarkDay = embarkAt ? String(embarkAt).slice(0, 10) : null;
    const disembarkDay = disembarkAt ? String(disembarkAt).slice(0, 10) : null;
    const list = [];
    if (embarkPort || embarkAt) {
      list.push({
        id: "embark", kind: "embark", label: "Embark",
        port: embarkPort || "—", at: embarkAt,
      });
    }
    const middle = (stops || [])
      .filter((s) => {
        if (!filterStopsToWindow) return true;
        const day = s.arrive_at ? String(s.arrive_at).slice(0, 10) : null;
        if (!day) return true;
        if (embarkDay && day < embarkDay) return false;
        if (disembarkDay && day > disembarkDay) return false;
        return true;
      })
      .map((s, i) => {
        const t = (s.stop_type === "anchor" || s.stop_type === "passage") ? s.stop_type : "port";
        const label = t === "anchor" ? "At Anchor" : t === "passage" ? "At Sea" : "On Port";
        return {
          id: `s-${s.id || i}`, kind: "stop", label,
          port: s.port || "—", at: s.arrive_at, depart_at: s.depart_at,
          country: s.country,
          lat: s.lat ?? null,
          lon: s.lon ?? null,
          sort_index: Number(s.sort_index || 0),
          stop_type: t,
        };
      });
    middle.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : null;
      const tb = b.at ? new Date(b.at).getTime() : null;
      if (ta != null && tb != null && ta !== tb) return ta - tb;
      if (ta != null && tb == null) return -1;
      if (ta == null && tb != null) return 1;
      return a.sort_index - b.sort_index;
    });
    middle.forEach((m) => list.push(m));
    if (disembarkPort || disembarkAt) {
      list.push({
        id: "disembark", kind: "disembark", label: "Disembark",
        port: disembarkPort || "—", at: disembarkAt,
      });
    }
    // Equal spacing: i / (n-1) × 100. Single node lands at 50%.
    const n = list.length;
    list.forEach((node, i) => {
      node.pos = n <= 1 ? 50 : (i / (n - 1)) * 100;
    });
    return list;
  }, [embarkPort, embarkAt, disembarkPort, disembarkAt, stops]);

  // Today's position along the visual line — interpolate between the two
  // chronological nodes surrounding "now" so it stays consistent with the
  // equal-spacing layout instead of the raw date-percentage.
  const progressPct = useMemo(() => {
    if (nodes.length < 2) return null;
    const now = Date.now();
    let prevIdx = -1;
    for (let i = 0; i < nodes.length; i += 1) {
      const t = nodes[i].at ? new Date(nodes[i].at).getTime() : null;
      if (t != null && t <= now) prevIdx = i;
    }
    if (prevIdx < 0) return null;
    if (prevIdx >= nodes.length - 1) return 100;
    const prev = nodes[prevIdx];
    const next = nodes[prevIdx + 1];
    const tPrev = prev.at ? new Date(prev.at).getTime() : null;
    const tNext = next.at ? new Date(next.at).getTime() : null;
    if (tPrev == null || tNext == null || tNext <= tPrev) return prev.pos;
    const frac = (now - tPrev) / (tNext - tPrev);
    return prev.pos + Math.max(0, Math.min(1, frac)) * (next.pos - prev.pos);
  }, [nodes]);

  // Per-node temporal phase — "past" (already sailed past), "current"
  // (today's location) or "future". Drives the size + colour treatment
  // so the guest can see at a glance where they are.
  const phaseFor = useMemo(() => {
    const todayISO = new Date().toISOString().slice(0, 10);
    // The "current" node is the last chronological node whose arrival day
    // is on or before today; if that node has a depart_at, today must
    // still be before or on that depart day to count as current.
    let currentIdx = -1;
    nodes.forEach((n, idx) => {
      if (!n.at) return;
      const arrive = n.at.slice(0, 10);
      const depart = (n.depart_at || n.at).slice(0, 10);
      if (arrive <= todayISO && todayISO <= depart) currentIdx = idx;
    });
    // If today falls strictly between two nodes (in transit) — no depart
    // was set on the earlier one — highlight the most recent arrived node.
    if (currentIdx < 0) {
      nodes.forEach((n, idx) => {
        if (!n.at) return;
        if (n.at.slice(0, 10) <= todayISO) currentIdx = idx;
      });
    }
    return (idx) => {
      if (idx === currentIdx) return "current";
      if (idx < currentIdx) return "past";
      return "future";
    };
  }, [nodes]);

  const centerCopy = (() => {
    if (embarkAt && new Date(embarkAt).getTime() > Date.now()) {
      const d = Math.ceil((new Date(embarkAt).getTime() - Date.now()) / 86_400_000);
      return { label: "Boarding in", value: `${d} ${d === 1 ? "day" : "days"}` };
    }
    if (disembarkAt && new Date(disembarkAt).getTime() < Date.now() - 86_400_000) {
      return { label: "Voyage complete", value: "safe travels" };
    }
    if (daysRemaining != null) {
      if (daysRemaining === 0) return { label: "Final day", value: "today" };
      return { label: "Days remaining", value: `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}` };
    }
    if (daysAboard != null) return { label: "Day of voyage", value: `Day ${daysAboard + 1}` };
    return { label: "Aboard", value: null };
  })();

  if (nodes.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative w-full"
      data-testid="journey-timeline"
    >
      {/* Progress copy pinned above the line, centered by default or over
          today's position when the voyage spans a real range. Hidden when
          the parent already renders it (e.g. iPad CabinView shows "ABOARD"
          in the tile's top-right corner). */}
      {!hideCopy && (
      <div
        className="mb-3 md:mb-4 text-center transition-all"
        style={progressPct != null ? { marginLeft: `${progressPct}%`, transform: "translateX(-50%)", display: "inline-block", position: "relative", left: 0 } : undefined}
        data-testid="journey-progress"
      >
        <div className="overline text-accent text-[10px]">{centerCopy.label}</div>
        {centerCopy.value && (
          <div className="font-display text-lg md:text-2xl tracking-tight leading-tight mt-0.5 whitespace-nowrap">
            {centerCopy.value}
          </div>
        )}
      </div>
      )}

      {/* Timeline row.
          Layout uses flexbox with `justify-between` on the node track so
          equal spacing is guaranteed by the browser — no calc() gymnastics.
          Endpoints anchor to the flex edges naturally; the line + progress
          + today-marker live inside an inset absolute container whose left
          and right match the endpoint radius, so % positions inside it map
          exactly to the visual span between endpoint centres. */}
      <div className="relative select-none" onMouseLeave={() => setLastTouched(null)}>
        {/* Inset track — width equals the space between the CENTRES of the
            first and last thumbnails (72px endpoints → 36px inset).
            One single 1-px line rendered with a hard-stop CSS gradient at
            `progressPct` so the past portion is `accent/70` and the future
            portion softly fades `accent/60 → border → accent/60`. Layering
            two h-px elements here used to cause sub-pixel doubling on the
            past segment (the "trembling" the eye picked up) — one element
            is inherently steadier. */}
        <div
          className="absolute left-9 right-9 h-px pointer-events-none"
          style={{
            top: "calc(50% - 0.5px)",
            background: progressPct != null
              ? `linear-gradient(to right, hsl(var(--accent) / 0.75) 0%, hsl(var(--accent) / 0.75) ${progressPct}%, hsl(var(--border)) ${progressPct}%, hsl(var(--accent) / 0.6) 100%)`
              : "linear-gradient(to right, hsl(var(--accent) / 0.6), hsl(var(--border)), hsl(var(--accent) / 0.6))",
          }}
          aria-hidden
        />
        {progressPct != null && (
          <div className="absolute left-9 right-9 pointer-events-none" style={{ top: "calc(50% - 0.5px)" }} aria-hidden>
            <div
              className="absolute h-3 w-3 rounded-full bg-accent shadow-[0_0_0_4px_rgba(0,0,0,0.05)]"
              style={{ left: `${progressPct}%`, top: "0.5px", transform: "translate(-50%, -50%)" }}
              data-testid="journey-today-marker"
              aria-label="today"
            />
          </div>
        )}

        {/* Flex node track — equal distance between every icon by construction.
            `items-end` aligns every thumbnail to the row baseline so the
            timeline connector line sits cleanly ABOVE all thumbnails, not
            across the smaller past ones (which used to slot the line
            visually above their labels). */}
        <div
          ref={containerRef}
          className={`relative flex items-end justify-between gap-2 ${compact ? "min-h-24 sm:min-h-28 md:min-h-32" : "min-h-40 sm:min-h-44 md:min-h-48"}`}
          data-testid="journey-node-track"
        >
          {nodes.map((n, i) => {
            const _idx = i;
            const isRaised = lastTouched === n.id;
            const isEnd = n.kind === "embark" || n.kind === "disembark";
            const phase = phaseFor(i);
            // Auto-shrink to prevent overlap on dense charters — a 17-stop
            // Mediterranean loop was cropping labels on desktop and pinching
            // thumbnails on iPad. Scale factor derived from node count so
            // thumbnails stay legible without wrapping. Endpoints and middle
            // stops share the SAME base size at higher densities so the
            // Embark/Disembark thumbnails visually align with everything
            // else on the row (labels sit on one clean baseline).
            const count = nodes.length;
            const density = count <= 6 ? 1
                          : count <= 10 ? 0.82
                          : count <= 14 ? 0.68
                          : count <= 18 ? 0.58
                          : 0.5;
            // Endpoints get only a tiny size boost (1.05×) at low density
            // and none at high density — the visual emphasis comes from
            // the ring color and label, not from towering size disparity.
            const baseSize = Math.round(60 * density * (isEnd && density > 0.7 ? 1.05 : 1));
            const size = phase === "current" ? Math.round(baseSize * 1.18)
                        : phase === "past" ? Math.round(baseSize * 0.86)
                        : baseSize;
            const z = isRaised ? 60 : (phase === "current" ? 30 : (isEnd ? 20 : 10 + i));
            // Fade + desaturate ports the yacht has already left behind so
            // "where we are now" reads at a glance.
            const pastTreatment = phase === "past" ? "opacity-70 saturate-[0.6]" : "";
            const labelTone = phase === "past" ? "text-muted-foreground/70" : phase === "current" ? "text-foreground" : "text-foreground";
            return (
              <button
                key={n.id}
                type="button"
                onClick={portMapEnabled ? () => setMapIndex(_idx) : undefined}
                onMouseEnter={() => setLastTouched(n.id)}
                onFocus={() => setLastTouched(n.id)}
                onTouchStart={() => setLastTouched(n.id)}
                className={`relative flex flex-col items-center shrink-0 focus:outline-none group transition-transform duration-200 hover:scale-[1.08] focus-visible:scale-[1.08] ${portMapEnabled ? "cursor-pointer" : "cursor-default"}`}
                style={{ zIndex: z }}
                data-testid={`journey-node-${n.id}`}
                data-phase={phase}
                aria-label={`${n.label} ${n.port}${n.at ? " on " + fmtDate(n.at) : ""}${portMapEnabled ? " — click to view on map" : ""}`}
              >
                <div
                  className={`relative rounded-full ring-4 ring-background transition-all duration-500 ${pastTreatment} ${isRaised ? "shadow-[0_10px_28px_-10px_rgba(0,0,0,0.35)]" : "shadow-none"}`}
                  style={{ width: size, height: size }}
                >
                  <PortThumbnail name={n.port || ""} size={size} testId={`journey-thumb-${n.id}`} imageOverride={n.image_url} />
                  {/* Golden shimmer ring — only for the current node. Two
                      layered motion.divs: a static thin gold border for
                      visibility, and an outer glow that pulses gently.
                      Opacity-only pulse (no scale) — the earlier
                      `scale: [1, 1.04, 1]` was reading as a "shake" on
                      dense timelines with a large thumbnail. */}
                  {phase === "current" && (
                    <>
                      <motion.div
                        className="absolute inset-0 rounded-full pointer-events-none"
                        style={{ boxShadow: "0 0 0 2px #d4af37 inset" }}
                        data-testid={`journey-current-ring-${n.id}`}
                        aria-hidden
                      />
                      <motion.div
                        className="absolute -inset-2 rounded-full pointer-events-none"
                        style={{ boxShadow: "0 0 22px 4px rgba(230, 185, 90, 0.55)" }}
                        animate={{ opacity: [0.4, 0.85, 0.4] }}
                        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                        aria-hidden
                      />
                    </>
                  )}
                </div>
                <div className="mt-2 text-center whitespace-nowrap">
                  {/* Icon-only label when the timeline is dense (11+ stops)
                      so the text row doesn't collide/wrap. Full text at
                      lower densities where there's plenty of room. */}
                  {(() => {
                    const StopIcon = n.kind === "embark" || n.kind === "disembark" ? Anchor
                                   : n.stop_type === "anchor" ? Anchor
                                   : n.stop_type === "passage" ? Waves
                                   : MapPin;
                    const toneCls = phase === "current" ? "text-[#c9992b]"
                                   : phase === "past" ? "text-muted-foreground/50"
                                   : "text-accent";
                    // Past nodes lose the overline styling — the wide
                    // letter-spacing was collision-prone with the timeline
                    // connector for shrunk-down past ports.
                    if (phase === "past") {
                      return (
                        <div className={`text-[8px] leading-none italic ${toneCls}`}>{n.label.toLowerCase()}</div>
                      );
                    }
                    if (count > 10) {
                      return (
                        <div className={`flex items-center justify-center ${toneCls}`} title={n.label} aria-label={n.label}>
                          <StopIcon className="h-3 w-3" strokeWidth={2.2} />
                        </div>
                      );
                    }
                    return (
                      <div className={`overline text-[9px] leading-none ${toneCls}`}>{n.label}</div>
                    );
                  })()}
                  <div className={`font-display text-xs sm:text-sm tracking-tight leading-tight mt-0.5 max-w-[7rem] truncate mx-auto ${labelTone}`}>{n.port}</div>
                  {n.at && <div className={`text-[10px] mt-0.5 ${phase === "past" ? "text-muted-foreground/60" : "text-muted-foreground"}`}>{fmtDate(n.at)}</div>}
                  {n.country && n.kind === "stop" && isRaised && (
                    <div className="text-[10px] text-muted-foreground italic">{n.country}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {portMapEnabled && (
        <PortMapDialog
          open={mapIndex != null}
          onClose={() => setMapIndex(null)}
          stops={nodes.map((n) => ({
            id: n.id,
            port: n.port,
            country: n.country || null,
            at: n.at,
            stop_type: n.stop_type || (n.kind === "stop" ? "port" : "port"),
            kind: n.kind,
            lat: n.lat ?? null,
            lon: n.lon ?? null,
          }))}
          initialIndex={mapIndex ?? 0}
        />
      )}
    </motion.div>
  );
}
